import { ReactElement } from 'react';
import { Tooltip, TooltipProps } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Text } from '@/components/atoms/text';
import { Box } from '@mui/material';
import { SafeMarkdown } from '@/components/atoms/markdown/safe';
import { useTranslation } from '@/hooks/useTranslation';

interface VariableInfoTooltipProps {
  description: string;
  source: string;
  period: string;
  open: boolean;
  onClose: () => void;
  width?: string;
  titleWidth?: string;
  titleMinWidth?: string;
  valueWidth?: string;
  placement?: TooltipProps['placement'];
  children: ReactElement;
}

const StyledTooltipContent = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(2),
  maxWidth: 300,
  boxShadow: theme.shadows[4],
}));

const InfoRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(1),
  marginBottom: theme.spacing(1),
  '&:last-child': {
    marginBottom: 0,
  },
}));

const InfoLabel = styled(Text)(({ theme }) => ({
  fontWeight: theme.typography.fontWeightMedium,
  minWidth: '64px',
  color: theme.palette.text.secondary,
}));

const InfoValue = styled(Text)(({ theme }) => ({
  color: theme.palette.text.primary,
  flex: 1,
}));

export const VariableInfoTooltip = ({
  description,
  source,
  period,
  open,
  onClose,
  placement = 'top',
  children,
}: VariableInfoTooltipProps) => {
  const { t } = useTranslation();

  const tooltipContent = (
    <StyledTooltipContent>
      <InfoRow>
        <InfoLabel variant="caption">{t('label.label.tooltip_description')}</InfoLabel>
        <InfoValue variant="caption">{description}</InfoValue>
      </InfoRow>
      <InfoRow>
        <InfoLabel variant="caption">{t('label.label.tooltip_source')}</InfoLabel>
        <InfoValue variant="caption">
          <SafeMarkdown text={source} />
        </InfoValue>
      </InfoRow>
      <InfoRow>
        <InfoLabel variant="caption">{t('label.label.tooltip_period')}</InfoLabel>
        <InfoValue variant="caption">{period}</InfoValue>
      </InfoRow>
    </StyledTooltipContent>
  );

  return (
    <Tooltip
      title={tooltipContent}
      open={open}
      onClose={onClose}
      placement={placement}
      arrow
      componentsProps={{
        tooltip: {
          sx: {
            bgcolor: 'transparent',
            padding: 0,
            maxWidth: 'none',
          },
        },
      }}
    >
      {children}
    </Tooltip>
  );
};
